import React, {
  useContext,
  useEffect,
  Fragment,
  useState,
  useMemo,
} from "react";
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  Row,
  Table,
  Badge,
  Alert,
} from "react-bootstrap";
import { ThemeContext } from "../../../context/ThemeContext";
import {
  useTable,
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
} from "react-table";
import moment from "moment";
import {
  getAwsGlueJobs,
  getEc2Instances,
  getAwsGlueJobsFromDb,
} from "../../../services/awsService";
import { useMsal } from "@azure/msal-react";
const ColumnFilter = ({ column }) => {
  const { filterValue, setFilter } = column;
  return (
    <input
      className="form-control input-search"
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value)}
    />
  );
};

const AwsGlueDashboard = () => {
  const { background } = useContext(ThemeContext);
  let awsToken = null;
  const { accounts } = useMsal();
  if (accounts.length > 0) {
    awsToken = accounts[0].idToken;
  }

  const username = useMemo(() => accounts[0]?.name ?? "Unknown", [accounts]);

  const titleColor = background.value === "dark" ? "#fff" : "#000";
  const [awsGlueJobs, setAwsGlueJobs] = useState([]);
  const [lastRefreshTime, setLastRefreshTime] = useState(
    moment().format("YYYY-MM-DD HH:mm:ss")
  );
  const [loadingElement, setLoadingElement] = useState(true);
  const [showEdmAlert, setShowEdmAlert] = useState(false);
  const [allEmrClusters, setAllEmrClusters] = useState([]);
  const [ec2Instances, setEc2Instances] = useState([]);
  const [allSqsQueues, setAllSqsQueues] = useState([]);
  const [appflowData, setAppflowData] = useState([]);
  const [ecsClusters, setEcsClusters] = useState([]);
  const [env, setEnv] = useState("prod");

  const handleRescan = async (selectedEnv = env, refresh = false) => {
    console.log("Starting rescan for environment:", selectedEnv, refresh);
    setLoadingElement(true);
    try {
      let jobsData = null;
      // if (awsToken) {
      //     console.log("AWS token is available, fetching AWS Glue Jobs...");
      //     jobsData = await getAwsGlueJobs(selectedEnv, awsToken);
      //     console.log("Fetched jobs data:", jobsData);
      // } else {
      //     console.warn("AWS token is not available.");
      // }
      console.log("AWS token is available, fetching AWS Glue Jobs...");
      console.log("Selected environment:", selectedEnv);
      jobsData = await getAwsGlueJobs(selectedEnv, refresh);
      console.log("Fetched jobs data:", jobsData);

      let glueJobs = [];
      let emrClusters = [];
      let sqsQueues = [];
      let ecsClusters = [];
      let appflowData = [];
      console.log("Initial SQS QUEUES data:", jobsData?.sqsData);

      if (jobsData?.glueJobs.length) {
        glueJobs = jobsData.glueJobs;
        console.log("Glue jobs data found:", glueJobs);
        setAwsGlueJobs(glueJobs);
      } else {
        console.warn("No Glue job run data available");
        setAwsGlueJobs([]);
      }

      if (jobsData?.emrClusters?.length) {
        emrClusters = jobsData.emrClusters;
        console.log("EMR clusters data found:", emrClusters);
        const updated = emrClusters.map((cluster) => ({
          ...cluster,
          icon: "fa-solid fa-server",
          color: "transparent",
          lastChecked: moment().format("YYYY-MM-DD HH:mm:ss"),
        }));
        console.log("Updated EMR clusters:", updated);
        setAllEmrClusters(updated);
      } else {
        console.warn("No EMR cluster data available");
        setAllEmrClusters([]);
      }

      if (jobsData?.ec2Instances?.length) {
        const ec2Instances = jobsData.ec2Instances;
        console.log("EC2 instances data found:", ec2Instances);
        const updated = ec2Instances.map((instance) => ({
          ...instance,
          icon: "fa-solid fa-server",
          color: "transparent",
          lastChecked: moment().format("YYYY-MM-DD HH:mm:ss"),
        }));
        console.log("Updated EC2 instances:", updated);
        setEc2Instances(updated);
      } else {
        console.warn("No EC2 instance data available");
        setEc2Instances([]);
      }

      if (jobsData?.sqsData?.length) {
        console.log("SQS data found:", jobsData.sqsData);
        setAllSqsQueues(jobsData.sqsData);
      } else {
        console.warn("No SQS queue data available");
        setAllSqsQueues([]);
      }

      if (jobsData?.appflowData?.length) {
        console.log("AppFlow data found:", jobsData.appflowData);
        setAppflowData(jobsData.appflowData);
      } else {
        console.warn("No AppFlow data available");
        setAppflowData([]);
      }

      if (jobsData?.ecsClusters?.length) {
        console.log("ECS clusters data found:", jobsData.ecsClusters);
        setEcsClusters(jobsData.ecsClusters);
      } else {
        console.warn("No ECS cluster data available");
        setEcsClusters([]);
      }

      const currentTime = moment().format("YYYY-MM-DD HH:mm:ss");
      console.log("Setting last refresh time to:", currentTime);
      setLastRefreshTime(currentTime);
    } catch (error) {
      console.error("Error during rescan:", error);
    } finally {
      console.log("Rescan process completed.");
      setLoadingElement(false);
    }
  };

  useEffect(() => {
    handleRescan();
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "Status",
        accessor: "status",
        Filter: ColumnFilter,
        Cell: ({ cell: { value } }) => {
          let statusClass = "";
          switch (value?.toUpperCase()) {
            case "SUCCEEDED":
              statusClass = "text-success";
              break;
            case "FAILED":
              statusClass = "text-danger";
              break;
            case "RUNNING":
              statusClass = "text-warning";
              break;
            case "STOPPED":
              statusClass = "text-secondary";
              break;
            default:
              statusClass = "text-primary";
          }
          return <span className={statusClass}>{value?.toUpperCase()}</span>;
        },
      },
      { Header: "Job Name", accessor: "jobName", Filter: ColumnFilter },
      { Header: "Run ID", accessor: "runId", Filter: ColumnFilter },
      {
        Header: "Started On",
        accessor: "startedOn",
        Filter: ColumnFilter,
        Cell: ({ cell: { value } }) =>
          value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "N/A",
      },
      {
        Header: "Completed On",
        accessor: "completedOn",
        Filter: ColumnFilter,
        Cell: ({ cell: { value } }) =>
          value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "N/A",
      },
      {
        Header: "Error Message",
        accessor: "errorMessage",
        Filter: ColumnFilter,
        Cell: ({ cell: { value } }) => (
          <div style={{ whiteSpace: "normal", maxWidth: "400px" }}>{value}</div>
        ),
      },
      {
        Header: "Execution Time (sec)",
        accessor: "executionTime",
        Filter: ColumnFilter,
      },
      { Header: "Glue Version", accessor: "glueVersion", Filter: ColumnFilter },
      { Header: "Worker Type", accessor: "workerType", Filter: ColumnFilter },
      {
        Header: "Number of Workers",
        accessor: "numberOfWorkers",
        Filter: ColumnFilter,
      },
      { Header: "Timeout (min)", accessor: "timeout", Filter: ColumnFilter },
    ],
    []
  );
  const ec2Columns = useMemo(
    () => [
      { Header: "Instance ID", accessor: "instanceId", Filter: ColumnFilter },
      { Header: "Name", accessor: "name", Filter: ColumnFilter },
      { Header: "State", accessor: "state", Filter: ColumnFilter },
      { Header: "Type", accessor: "type", Filter: ColumnFilter },
      { Header: "AZ", accessor: "az", Filter: ColumnFilter },
      {
        Header: "Launch Time",
        accessor: "launchTime",
        Filter: ColumnFilter,
        Cell: ({ cell: { value } }) =>
          value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "N/A",
      },
      { Header: "Private IP", accessor: "privateIp", Filter: ColumnFilter },
      { Header: "Public IP", accessor: "publicIp", Filter: ColumnFilter },
      {
        Header: "CPU (%)",
        accessor: "metrics.cpuUtilization",
        Filter: ColumnFilter,
        Cell: ({ cell: { value } }) =>
          value ? `${(value * 100).toFixed(2)}%` : "N/A",
      },
      {
        Header: "Net In (bytes)",
        accessor: "metrics.networkInBytes",
        Filter: ColumnFilter,
      },
      {
        Header: "Net Out (bytes)",
        accessor: "metrics.networkOutBytes",
        Filter: ColumnFilter,
      },
    ],
    []
  );

  const sqsColumns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "queueName",
        Filter: ColumnFilter,
      },
      {
        Header: "Created On",
        accessor: "createdDate",
        Filter: ColumnFilter,
      },
      {
        Header: "Messages Avaiable",
        accessor: "messagesAvailable",
        Filter: ColumnFilter,
      },
      {
        Header: "Messages in Flight",
        accessor: "messagesInFlight",
        Filter: ColumnFilter,
      },
    ],
    []
  );

  const appflowColumns = useMemo(
    () => [
      { Header: "Flow Name", accessor: "flowName", Filter: ColumnFilter },
      {
        Header: "Flow Status",
        accessor: "flowStatus",
        Filter: ColumnFilter,
        Cell: ({ cell: { value } }) => {
          let statusClass = "";
          switch ((value || "").toUpperCase()) {
            case "ACTIVE":
            case "ENABLED":
            case "SUCCEEDED":
            case "SUCCESS":
              statusClass = "text-success";
              break;
            case "FAILED":
            case "ERROR":
            case "INACTIVE":
            case "DISABLED":
              statusClass = "text-danger";
              break;
            case "RUNNING":
            case "IN PROGRESS":
              statusClass = "text-warning";
              break;
            default:
              statusClass = "text-primary";
          }
          return <span className={statusClass}>{value}</span>;
        },
      },
      { Header: "Source", accessor: "source", Filter: ColumnFilter },
      { Header: "Destination", accessor: "destination", Filter: ColumnFilter },
      { Header: "Trigger", accessor: "trigger", Filter: ColumnFilter },
      { Header: "Date Created", accessor: "dateCreated", Filter: ColumnFilter },
      { Header: "Created By", accessor: "createdBy", Filter: ColumnFilter },
      { Header: "Last Run", accessor: "lastRun", Filter: ColumnFilter },
      {
        Header: "Run Status",
        accessor: "runStatus",
        Filter: ColumnFilter,
        Cell: ({ cell: { value } }) => {
          let statusClass = "";
          switch ((value || "").toUpperCase()) {
            case "SUCCEEDED":
            case "SUCCESS":
              statusClass = "text-success";
              break;
            case "FAILED":
            case "ERROR":
              statusClass = "text-danger";
              break;
            case "RUNNING":
            case "IN PROGRESS":
              statusClass = "text-warning";
              break;
            default:
              statusClass = "text-primary";
          }
          return <span className={statusClass}>{value}</span>;
        },
      },
    ],
    []
  );

  const data = useMemo(() => awsGlueJobs || [], [awsGlueJobs]);
  const tableInstance = useTable(
    { columns, data },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    pageCount,
    gotoPage,
    state: { pageIndex },
  } = tableInstance;

  const ec2TableInstance = useTable(
    { columns: ec2Columns, data: ec2Instances },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps: getEc2TableProps,
    getTableBodyProps: getEc2BodyProps,
    headerGroups: ec2HeaderGroups,
    prepareRow: prepareEc2Row,
    page: ec2Page,
    nextPage: ec2NextPage,
    previousPage: ec2PreviousPage,
    canNextPage: ec2CanNextPage,
    canPreviousPage: ec2CanPreviousPage,
    pageOptions: ec2PageOptions,
    pageCount: ec2PageCount,
    gotoPage: ec2GotoPage,
    state: { pageIndex: ec2PageIndex },
  } = ec2TableInstance;

  const sqsTableInstance = useTable(
    { columns: sqsColumns, data: allSqsQueues },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const {
    getTableProps: getSqsTableProps,
    getTableBodyProps: getSqsBodyProps,
    headerGroups: sqsHeaderGroups,
    prepareRow: prepareSqsRow,
    page: sqsPage,
    nextPage: sqsNextPage,
    previousPage: sqsPreviousPage,
    canNextPage: sqsCanNextPage,
    canPreviousPage: sqsCanPreviousPage,
    pageOptions: sqsPageOptions,
    pageCount: sqsPageCount,
    gotoPage: sqsGotoPage,
    state: { pageIndex: sqsPageIndex },
  } = sqsTableInstance;

  const appflowTableInstance = useTable(
    { columns: appflowColumns, data: appflowData },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const {
    getTableProps: getAppflowTableProps,
    getTableBodyProps: getAppflowBodyProps,
    headerGroups: appflowHeaderGroups,
    prepareRow: prepareAppflowRow,
    page: appflowPage,
    nextPage: appflowNextPage,
    previousPage: appflowPreviousPage,
    canNextPage: appflowCanNextPage,
    canPreviousPage: appflowCanPreviousPage,
    pageOptions: appflowPageOptions,
    pageCount: appflowPageCount,
    gotoPage: appflowGotoPage,
    state: { pageIndex: appflowPageIndex },
  } = appflowTableInstance;

  return (
    <Fragment>
      <div className="row pt-5">
        {/* <h1 className="title text-center"> AWS Trust Portal</h1> */}
      </div>
      <div className="row justify-content-center">
        <div className="col-md-6">
          <ButtonGroup className="env-toggle">
            <Button
              variant={env === "prod" ? "primary" : "outline-primary"}
              onClick={() => {
                setEnv("prod");
                handleRescan("prod", false);
              }}
              className={env === "prod" ? "active" : ""}
            >
              PRD
            </Button>
            <Button
              variant={env === "dev" ? "primary" : "outline-primary"}
              onClick={() => {
                setEnv("dev");
                handleRescan("dev", false);
              }}
              className={env === "dev" ? "active" : ""}
            >
              DEV
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <style jsx>{`
        .env-toggle {
          display: flex;
          justify-content: center;
          margin: 20px 0;
        }
        .env-toggle .btn {
          min-width: 120px;
          transition: all 0.3s ease;
          border-radius: 0;
        }
        .env-toggle .btn:first-child {
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
        }
        .env-toggle .btn:last-child {
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }
        .env-toggle .btn.active {
          font-weight: bold;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      `}</style>

      <Row className="mt-5 bubble-cluster-container">
        {allEmrClusters.map((item, index) => (
          <div
            key={index}
            className={`bubble bubble-large ${
              parseFloat(item.memoryUsagePercentage) > 150
                ? "critical-memory"
                : parseFloat(item.memoryUsagePercentage) > 100
                ? "warning-memory"
                : ""
            }`}
          >
            <div className="bubble-content">
              <div className="bubble-icon">
                <i className={item.icon}></i>
              </div>

              <div className="bubble-title">{item.clusterName}</div>

              <div className="bubble-status">
                <span
                  className={
                    item.status === "RUNNING"
                      ? "text-success"
                      : item.status === "FAILED"
                      ? "text-danger"
                      : "text-warning"
                  }
                >
                  {item.status}
                </span>
              </div>

              <div className="health-info-container">
                <div
                  className="health-info-item"
                  data-tooltip={`Container Health is based on percentage of allocated containers < 95%: ${
                    item.ContainerHealth.additionalLabel || ""
                  } | Allocated: ${item.ContainerHealth.value || ""}`}
                >
                  <div className="health-label">Container Health</div>
                  <div
                    className={(() => {
                      // Support both object and string for ContainerHealth
                      const ch = item.ContainerHealth;
                      if (typeof ch === "string") {
                        if (ch === "CRITICAL")
                          return "health-value text-danger";
                        if (ch === "UNKNOWN")
                          return "health-value text-warning";
                        if (ch === "HEALTHY")
                          return "health-value text-success";
                        return "health-value";
                      }
                      if (ch?.status === "CRITICAL")
                        return "health-value text-danger";
                      if (ch?.status === "UNKNOWN")
                        return "health-value text-warning";
                      return "health-value text-success";
                    })()}
                  >
                    {(() => {
                      // If ContainerHealth is a string, just show it
                      if (typeof item.ContainerHealth === "string") {
                        return item.ContainerHealth;
                      }
                      // Otherwise, show status, percentage, value if present
                      const ch = item.ContainerHealth || {};
                      return (
                        <>
                          {ch.status || ""}
                          {ch.percentage ? ` (${ch.percentage}%)` : ""}
                          {ch.value ? `  (${ch.value})` : ""}
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div
                  className="health-info-item"
                  data-tooltip="Memory health is based the % of memory usage less than 80% is healthy"
                >
                <div className="health-label">YARNMemory Health</div>
                <div
                  className={(() => {
                    const mh = item.MemoryHealth;
                    if (typeof mh === "string") {
                      if (mh === "CRITICAL")
                        return "health-value text-danger";
                      if (mh === "UNKNOWN")
                        return "health-value text-warning";
                      if (mh === "HEALTHY")
                        return "health-value text-success";
                      return "health-value";
                    }
                    if (mh?.status === "CRITICAL")
                      return "health-value text-danger";
                    if (mh?.status === "UNKNOWN")
                      return "health-value text-warning";
                    return "health-value text-success";
                  })()}
                >
                  {(() => {
                    if (typeof item.MemoryHealth === "string") {
                      return item.MemoryHealth;
                    }
                    const mh = item.MemoryHealth || {};
                    return (
                      <>
                        {mh.status || ""}
                        {mh.percentage ? ` (${mh.percentage}%)` : ""}
                        {/* {mh.value ? `  (${mh.value}MB)` : ""} */}
                      </>
                    );
                  })()}

                </div>
                </div>
                <div
                  className="health-info-item"
                  data-tooltip="HDFS health is based on the % of HDFS usage less than 80% is healthy"
                >
                <div className="health-label">HDFS Health </div>
                <div
                    className={(() => {
                      const mh = item.HDFSHealth;
                      if (typeof mh === "string") {
                        if (mh === "CRITICAL")
                          return "health-value text-danger";
                        if (mh === "UNKNOWN")
                          return "health-value text-warning";
                        if (mh === "HEALTHY")
                          return "health-value text-success";
                        return "health-value";
                      }
                      if (mh?.status === "CRITICAL")
                        return "health-value text-danger";
                      if (mh?.status === "UNKNOWN")
                        return "health-value text-warning";
                      return "health-value text-success";
                    })()}
                  >
                    {(() => {
                      if (typeof item.HDFSHealth === "string") {
                        return item.HDFSHealth;
                      }
                      const mh = item.HDFSHealth || {};
                      return (
                        <>
                          {mh.status || ""}
                          {mh.percentage ? ` (${mh.percentage}%)` : ""}
                        </>
                      );
                    })()}

                </div>
                </div>
                <div
                  className="health-info-item"
                  data-tooltip="EMR Master Memory health is based on the % of memory usage in the EMR Master node, less than 80% is healthy"
                >
                <div className="health-label">EMR Master Memory</div>
                <div
                    className={(() => {
                      const mm = item.MemUsed;
                      if (typeof mm === "string") {
                        if (mm === "CRITICAL")
                          return "health-value text-danger";
                        if (mm === "UNKNOWN")
                          return "health-value text-warning";
                        if (mm === "HEALTHY")
                          return "health-value text-success";
                        return "health-value";
                      }
                      if (mm?.status === "CRITICAL")
                        return "health-value text-danger";
                      if (mm?.status === "UNKNOWN")
                        return "health-value text-warning";
                      return "health-value text-success";
                    })()}
                  >
                    {(() => {
                      if (typeof item.MemUsed === "string") {
                        return item.MemUsed;
                      }
                      const mm = item.MemUsed || {};
                      return (
                        <>
                          {mm.status || ""}
                          {mm.percentage ? ` (${mm.percentage}%)` : ""}
                        </>
                      );
                    })()}
                </div>
                </div>
              </div>

              <style jsx>{`
                .health-info-container {
                  display: flex;
                  flex-direction: column;
                  gap: 1rem;
                  padding: 1rem;
                }

                .health-info-item {
                  position: relative;
                  padding: 0.75rem;
                  border-radius: 8px;
                  background: rgba(255, 255, 255, 0.1);
                  transition: all 0.3s ease;
                  cursor: pointer;
                }

                .health-info-item:hover {
                  transform: translateY(-2px);
                  background: rgba(255, 255, 255, 0.2);
                  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }

                .health-info-item:hover::after {
                  content: attr(data-tooltip);
                  position: absolute;
                  bottom: 100%;
                  left: 50%;
                  transform: translateX(-50%);
                  padding: 0.5rem 1rem;
                  background: rgba(0, 0, 0, 0.8);
                  color: white;
                  border-radius: 4px;
                  font-size: 0.875rem;
                  white-space: nowrap;
                  z-index: 1000;
                  pointer-events: none;
                }

                .health-label {
                  font-size: 0.875rem;
                  color: #666;
                  margin-bottom: 0.25rem;
                }

                .health-value {
                  font-size: 1rem;
                  font-weight: 600;
                }
              `}</style>
            </div>
          </div>
        ))}
      </Row>

      <style jsx>{`
        .bubble-cluster-container {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 2rem;
          padding: 2rem;
        }
        .bubble {
          width: 500px;
          height: 500px;
          border-radius: 50%;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #3d4465;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .bubble.warning-memory {
          border: 2px solid #ffa500;
        }
        .bubble.critical-memory {
          border: 2px solid #ff0000;
        }
        .bubble:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }
        .bubble-content {
          text-align: center;
          padding: 10px;
          color: #3d4465;
        }
        .bubble-icon {
          font-size: 2rem;
          margin-bottom: 0.5rem;
        }
        .bubble-title {
          font-weight: 600;
          font-size: 1.1rem;
          margin-bottom: 0.25rem;
        }
        .bubble-status {
          margin-bottom: 0.25rem;
          font-size: 0.95rem;
        }
        .bubble-info {
          font-size: 0.85rem;
          margin-top: 1rem;
        }
      `}</style>
      <Row className="mt-5 bubble-cluster-container">
        <div className="bubble-cluster-container">
          <h1 className="bubble-title" style={{ fontSize: '2rem' }}>ECS Clusters</h1>
        </div>
        {ecsClusters.map((item, index) =>
          item.services.map((service, idx) => (
            <div
              key={`${index}-${idx}`}
              className={`bubble ${
                parseFloat(item.memoryUsagePercentage) > 150
                  ? "critical-memory"
                  : parseFloat(item.memoryUsagePercentage) > 100
                  ? "warning-memory"
                  : ""
              }`}
            >
              <div className="bubble-content">
                <div className="bubble-icon">
                  <i className="fas fa-server"></i>
                </div>
                <div className="bubble-title">
                  {item.clusterName}
                </div>

                <div className="bubble-status">
                  <span
                    className={
                      service.status === "RUNNING"
                        ? "text-success"
                        : service.status === "FAILED"
                        ? "text-danger"
                        : service.status === "ACTIVE"
                        ? "text-success"
                        : "text-warning"
                    }
                  >
                    {service.serviceName}
                  </span>
                </div>

                <div className="bubble-info">
                  <span
                    className={
                      service.status === "RUNNING"
                        ? "text-success"
                        : service.status === "FAILED"
                        ? "text-danger"
                        : service.status === "ACTIVE"
                        ? "text-success"
                        : "text-warning"
                    }
                  >
                    {service.status}
                  </span>
                </div>

                <div className="bubble-info">
                  <div className="health-info-item" data-tooltip={`CPU Health: CRITICAL > 80%`}>
                    <div className="health-label">CPU Health</div>
                    <div className={`health-value ${service.CPUHealth.status === "HEALTHY" ? "text-success" : "text-danger"}`}>
                      {service.CPUHealth.status} ({service.CPUHealth.percentage}%)
                    </div>
                  </div>
                  <div className="health-info-item" data-tooltip={`Memory Health: CRITICAL > 80%`}>
                    <div className="health-label">Memory Health</div>
                    <div className={`health-value ${service.MemoryHealth.status === "HEALTHY" ? "text-success" : "text-danger"}`}>
                      {service.MemoryHealth.status} ({service.MemoryHealth.percentage}%)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </Row>

      <style jsx>{`
        .bubble-cluster-container {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 2rem;
          padding: 2rem;
        }
        .bubble {
          width: 500px;
          height: 500px;
          border-radius: 50%;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #3d4465;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .bubble.warning-memory {
          border: 2px solid #ffa500;
        }
        .bubble.critical-memory {
          border: 2px solid #ff0000;
        }
        .bubble:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }
        .bubble-content {
          text-align: center;
          padding: 10px;
          color: #3d4465;
        }
        .bubble-icon {
          font-size: 2rem;
          margin-bottom: 0.5rem;
        }
        .bubble-title {
          font-weight: 600;
          font-size: 1.1rem;
          margin-bottom: 0.25rem;
        }
        .bubble-status {
          margin-bottom: 0.25rem;
          font-size: 0.95rem;
        }
        .bubble-info {
          font-size: 0.85rem;
          margin-top: 1rem;
        }
      `}</style>

      {/* <Row className='mt-5'>
                {allEmrClusters.map((item, index) => (
                    <Col xl={3} xxl={3} lg={4} md={6} sm={12} key={index} className="mb-4">
                        <div className="card shadow-sm p-3 d-flex flex-column h-100">
                            <div className="d-flex align-items-center mb-2">
                                <div className="me-3">
                                    <span style={{ fontSize: "2.2rem" }}><i className={item.icon}></i></span>
                                </div>
                                <div>
                                    <h5 className="mb-1" style={{ fontWeight: 600 }}>{item.name}</h5>
                                    <span className={item.status === "RUNNING" ? "text-success" : item.status === "FAILED" ? "text-danger" : "text-warning"}>{item.status}</span>
                                </div>
                            </div>
                            <div>
                                <div><strong>Hours:</strong> {item.normalizedInstanceHours}</div>
                                <div><strong>Release:</strong> {item.releaseLabel}</div>
                                <div><strong>Owner:</strong> {item.applicationOwner}</div>
                                <div><strong>Env:</strong> {item.environment}</div>
                                <div><strong>Created:</strong> {moment(item.createdOn).format("YYYY-MM-DD HH:mm")}</div>
                                <div><strong>DNS:</strong> <span style={{ fontSize: "0.8rem" }}>{item.masterPublicDnsName}</span></div>
                                <div className="mt-2 text-muted" style={{ fontSize: "0.75rem" }}>Last checked: {item.lastChecked}</div>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row> */}
      <Row className="justify-content-center mt-3 mb-4 px-4">
        <Col xs={12} md={8} lg={4}>
          <Button
            variant="primary"
            className="w-100"
            onClick={() => handleRescan("prod", true)}
          >
            Rescan All Data
          </Button>
        </Col>
      </Row>
      <Col className="mt-5" lg={12}>
        <Card>
          <Card.Header>
            <Card.Title>AWS SQS </Card.Title>
            {lastRefreshTime && (
              <div
                style={{
                  textAlign: "center",
                  fontSize: "0.9rem",
                  color: "#3d4465",
                }}
              >
                Last refreshed: {lastRefreshTime}
              </div>
            )}
          </Card.Header>
          <Card.Body>
            <Table {...getTableProps()} responsive>
              <thead>
                {sqsHeaderGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        style={{ cursor: "pointer", verticalAlign: "top" }}
                      >
                        {column.render("Header")}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " 🔽"
                              : " 🔼"
                            : ""}
                        </span>
                        {column.canFilter ? (
                          <div>{column.render("Filter")}</div>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getSqsBodyProps()}>
                {sqsPage.map((row) => {
                  prepareSqsRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          style={{ verticalAlign: "top" }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
          <div className="d-flex flex-column align-items-end mt-3 px-4">
            <span>
              Page{" "}
              <strong>
                {sqsPageIndex + 1} of {sqsPageOptions.length}
              </strong>{" "}
            </span>
            <span className="table-index mt-4">
              Go to page:{" "}
              <input
                type="number"
                className="ml-2"
                defaultValue={sqsPageIndex + 1}
                onChange={(e) => {
                  const pageNumber = e.target.value
                    ? Number(e.target.value) - 1
                    : 0;
                  gotoPage(pageNumber);
                }}
                style={{ width: "60px", marginLeft: "5px" }}
              />
            </span>
          </div>
          <div className="text-center px-4">
            <div className="filter-pagination mt-3">
              <button
                className="previous-button"
                onClick={() => sqsGotoPage(0)}
                disabled={!sqsCanPreviousPage}
              >
                {"<<"}
              </button>
              <button
                className="previous-button"
                onClick={() => sqsPreviousPage()}
                disabled={!sqsCanPreviousPage}
              >
                Previous
              </button>
              <button
                className="next-button"
                onClick={() => sqsNextPage()}
                disabled={!sqsCanNextPage}
              >
                Next
              </button>
              <button
                className="next-button"
                onClick={() => sqsGotoPage(sqsPageCount - 1)}
                disabled={!sqsCanNextPage}
              >
                {">>"}
              </button>
            </div>
          </div>
        </Card>
      </Col>

      <Col className="mt-5" lg={12}>
        <Card>
          <Card.Header>
            <Card.Title>AWS AppFlow</Card.Title>
          </Card.Header>
          <Card.Body>
            <Table {...getAppflowTableProps()} responsive>
              <thead>
                {appflowHeaderGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        style={{ cursor: "pointer", verticalAlign: "top" }}
                      >
                        {column.render("Header")}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " 🔽"
                              : " 🔼"
                            : ""}
                        </span>
                        {column.canFilter ? (
                          <div>{column.render("Filter")}</div>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getAppflowBodyProps()}>
                {appflowPage.map((row) => {
                  prepareAppflowRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          style={{ verticalAlign: "top" }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Col>
      <Col className="mt-5" lg={12}>
        <Card>
          <Card.Header>
            <Card.Title>AWS Glue Job Runs</Card.Title>
            {lastRefreshTime && (
              <div
                style={{
                  textAlign: "center",
                  fontSize: "0.9rem",
                  color: "#3d4465",
                }}
              >
                Last refreshed: {lastRefreshTime}
              </div>
            )}
          </Card.Header>
          <Card.Body>
            <Table {...getTableProps()} responsive>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        style={{ cursor: "pointer", verticalAlign: "top" }}
                      >
                        {column.render("Header")}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " 🔽"
                              : " 🔼"
                            : ""}
                        </span>
                        {column.canFilter ? (
                          <div>{column.render("Filter")}</div>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          style={{ verticalAlign: "top" }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
          <div className="d-flex flex-column align-items-end mt-3 px-4">
            <span>
              Page{" "}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>{" "}
            </span>
            <span className="table-index mt-4">
              Go to page:{" "}
              <input
                type="number"
                className="ml-2"
                defaultValue={pageIndex + 1}
                onChange={(e) => {
                  const pageNumber = e.target.value
                    ? Number(e.target.value) - 1
                    : 0;
                  gotoPage(pageNumber);
                }}
                style={{ width: "60px", marginLeft: "5px" }}
              />
            </span>
          </div>
          <div className="text-center px-4">
            <div className="filter-pagination mt-3">
              <button
                className="previous-button"
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              >
                {"<<"}
              </button>
              <button
                className="previous-button"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                Previous
              </button>
              <button
                className="next-button"
                onClick={() => nextPage()}
                disabled={!canNextPage}
              >
                Next
              </button>
              <button
                className="next-button"
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              >
                {">>"}
              </button>
            </div>
          </div>
        </Card>
      </Col>
      {loadingElement && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1050,
          }}
        >
          <div
            style={{
              width: "3rem",
              height: "3rem",
              border: "0.4rem solid rgba(0, 0, 255, 0.3)",
              borderTop: "0.4rem solid blue",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
        </div>
      )}
    </Fragment>
  );
};

export default AwsGlueDashboard;
